import { COMPANY_DETAILS } from "@vahak/core/dist/constants";
import { IMG_BASE_URL } from "@vahak/core/dist/constants/misc";

import { getLcvRouteBySlug } from "../components/lcv-route-landing/data";
import { getCityPageBySlug } from "../components/city/data";
import { getFaqSchemaOrgJson } from "./getFaqSchemaOrgJson";

const LOCALE_PATH_PREFIX = /^\/(en|hi|te|gu|kn|mr|bn|ta)(?=\/|$)/;
const DEFAULT_HREFLANG_LOCALES = ["en", "hi"] as const;
const DEFAULT_OG_IMAGE = `${IMG_BASE_URL}/web_og_image_jan_22.png`;
const LCV_BOOKING_PATH = "/lcv-booking";
const LCV_BOOKING_PREFIX = `${LCV_BOOKING_PATH}/`;
const CITY_PATH = "/city";
const CITY_PREFIX = `${CITY_PATH}/`;

const HREFLANG_BY_LOCALE: Record<string, string> = {
    bn: "bn-IN",
    en: "en-IN",
    gu: "gu-IN",
    hi: "hi-IN",
    kn: "kn-IN",
    mr: "mr-IN",
    ta: "ta-IN",
    te: "te-IN"
};

export interface PageSeoFaqItem {
    question: string;
    answer: string;
}

export interface PageSeoBreadcrumbItem {
    name: string;
    path?: string;
    url?: string;
}

export interface PageSeoSchemaConfig {
    type?: "Organization" | "WebPage" | "none";
    includeDefault?: boolean;
    path?: string;
    breadcrumbItems?: PageSeoBreadcrumbItem[];
    faqList?: PageSeoFaqItem[];
    extraJsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export interface PageSeoConfig {
    title?: string;
    description?: string;
    keywords?: string;
    asPath?: string;
    locale?: string;
    localeNeutralPath?: string;
    hreflangLocales?: string[];
    canonicalUrl?: string;
    ogUrl?: string;
    ogDescription?: string;
    ogImgSrc?: string;
    ogImgAltText?: string;
    ogType?: string;
    ogSiteName?: string;
    shouldBeIndexed?: boolean;
    schema?: PageSeoSchemaConfig;
    breadcrumbList?: string;
    faqList?: PageSeoFaqItem[];
    includeDefaultSchema?: boolean;
    schemaType?: "Organization" | "WebPage";
    schemaOrgJsonUrl?: string;
    cityName?: string;
}

interface ResolvedPageSeo {
    title?: string;
    description?: string;
    keywords?: string;
    canonicalUrl: string;
    ogUrl: string;
    ogDescription?: string;
    ogImgSrc: string;
    ogImgAltText?: string;
    ogType?: string;
    ogSiteName?: string;
    ogLocale?: string;
    ogLocaleAlternates: string[];
    shouldBeIndexed: boolean;
    hreflangAlternates: Array<{ hreflang: string; href: string }>;
    hreflangDefault: string;
    schemaScripts: Array<{ id: string; json: string }>;
}

function getLocaleHreflang(locale: string) {
    return HREFLANG_BY_LOCALE[locale] ?? "en-IN";
}

function getLocaleOgLocale(locale: string) {
    return getLocaleHreflang(locale).replace("-", "_");
}

function getCanonicalPath(asPath: string) {
    const clean = asPath
        .replace(/^\/en\//, "/")
        .replace(/^\/(web|mob)(?=\/|$)/, "")
        .replace(/\/$/, "")
        .split("?")[0]
        .split("#")[0];

    return clean || "/";
}

function getLocaleNeutralPath(asPath: string) {
    const path = getCanonicalPath(asPath).replace(LOCALE_PATH_PREFIX, "");
    return path || "/";
}

function getLocaleNeutralPathFromCanonicalUrl(canonicalUrl: string) {
    const website = COMPANY_DETAILS.website.replace(/\/$/, "");

    if (!canonicalUrl.startsWith(website)) {
        return undefined;
    }

    const path = canonicalUrl.slice(website.length).split("?")[0].split("#")[0];

    if (!path || path === "/") {
        return "/";
    }

    return path.startsWith("/") ? path : `/${path}`;
}

function resolveLocaleNeutralPath(config: PageSeoConfig) {
    if (config.localeNeutralPath) {
        return config.localeNeutralPath.startsWith("/") ? config.localeNeutralPath : `/${config.localeNeutralPath}`;
    }

    if (config.asPath) {
        return getLocaleNeutralPath(config.asPath);
    }

    if (config.canonicalUrl) {
        return getLocaleNeutralPathFromCanonicalUrl(config.canonicalUrl) ?? "/";
    }

    return "/";
}

function getLocalizedPageUrl(locale: string, localeNeutralPath: string, website = COMPANY_DETAILS.website) {
    if (locale === "en") {
        return localeNeutralPath === "/" ? website : `${website}${localeNeutralPath}`;
    }

    return localeNeutralPath === "/" ? `${website}/${locale}` : `${website}/${locale}${localeNeutralPath}`;
}

function resolveCanonicalUrl(config: PageSeoConfig, locale: string, localeNeutralPath: string) {
    if (locale === "en" && config.canonicalUrl) {
        return config.canonicalUrl;
    }

    return getLocalizedPageUrl(locale, localeNeutralPath);
}

function getHreflangAlternates(localeNeutralPath: string, locales: readonly string[]) {
    return locales.map((locale) => ({
        hreflang: getLocaleHreflang(locale),
        href: getLocalizedPageUrl(locale, localeNeutralPath)
    }));
}

/** Add new SEO landing route families here. */
function getRouteSeoDefaults(localeNeutralPath: string) {
    if (localeNeutralPath === LCV_BOOKING_PATH) {
        return {
            hreflangLocales: DEFAULT_HREFLANG_LOCALES,
            schemaType: "WebPage" as const
        };
    }

    if (localeNeutralPath.startsWith(LCV_BOOKING_PREFIX)) {
        const slug = localeNeutralPath.slice(LCV_BOOKING_PREFIX.length).split("/")[0];
        const route = slug ? getLcvRouteBySlug(slug) : undefined;

        if (route) {
            return {
                hreflangLocales: route.hreflangLocales ?? ["en"],
                schemaType: "WebPage" as const
            };
        }
    }

    if (localeNeutralPath.startsWith(CITY_PREFIX)) {
        const slug = localeNeutralPath.slice(CITY_PREFIX.length).split("/")[0];
        const city = slug ? getCityPageBySlug(slug) : undefined;

        if (city) {
            return {
                hreflangLocales: city.hreflangLocales ?? ["en"],
                schemaType: "WebPage" as const
            };
        }
    }

    return {
        hreflangLocales: DEFAULT_HREFLANG_LOCALES,
        schemaType: "WebPage" as const
    };
}

function appendBreadcrumbSchema(schemaScripts: ResolvedPageSeo["schemaScripts"], breadcrumbJson?: string) {
    if (!breadcrumbJson) {
        return;
    }

    schemaScripts.push({
        id: "jsonld-breadcrumb",
        json: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: JSON.parse(breadcrumbJson)
        })
    });
}

function breadcrumbListJson(items: PageSeoBreadcrumbItem[], locale: string, localeNeutralPathForCurrent?: string) {
    return JSON.stringify(
        items.map((item, index) => {
            let itemUrl = item.url;

            if (!itemUrl && item.path) {
                itemUrl = getLocalizedPageUrl(locale, item.path);
            }

            if (!itemUrl && index === items.length - 1 && localeNeutralPathForCurrent) {
                itemUrl = getLocalizedPageUrl(locale, localeNeutralPathForCurrent);
            }

            return {
                "@type": "ListItem",
                position: index + 1,
                name: item.name,
                ...(itemUrl ? { item: itemUrl } : {})
            };
        })
    );
}

export function getDefaultOgImage() {
    return DEFAULT_OG_IMAGE;
}

export function getHomeUrl() {
    return `${COMPANY_DETAILS.website}/`;
}

export function buildPageSeo(
    input: PageSeoConfig | null,
    fallback: { asPath: string; locale: string }
): ResolvedPageSeo | null {
    const config = input ? { locale: fallback.locale, asPath: fallback.asPath, ...input } : null;

    if (!config?.title && !config?.description) {
        const localeNeutralPath = getLocaleNeutralPath(fallback.asPath);
        const { hreflangLocales: routeHreflangLocales } = getRouteSeoDefaults(localeNeutralPath);
        const hreflangLocales = [...routeHreflangLocales];
        const canonicalUrl = getLocalizedPageUrl(fallback.locale, localeNeutralPath);

        return {
            canonicalUrl,
            ogUrl: canonicalUrl,
            ogImgSrc: DEFAULT_OG_IMAGE,
            shouldBeIndexed: config?.shouldBeIndexed ?? true,
            ogLocaleAlternates: [],
            hreflangAlternates: getHreflangAlternates(localeNeutralPath, hreflangLocales),
            hreflangDefault: getLocalizedPageUrl("en", localeNeutralPath),
            schemaScripts: []
        };
    }

    const locale = config.locale ?? fallback.locale;
    const localeNeutralPath = resolveLocaleNeutralPath(config);
    const routeDefaults = getRouteSeoDefaults(localeNeutralPath);
    const canonicalUrl = resolveCanonicalUrl(config, locale, localeNeutralPath);
    const ogUrl = locale === "en" && config.ogUrl ? config.ogUrl : canonicalUrl;
    const ogDescription = config.ogDescription ?? config.description;
    const ogImgSrc = config.ogImgSrc ?? DEFAULT_OG_IMAGE;
    const hreflangLocales = [...(config.hreflangLocales ?? routeDefaults.hreflangLocales)];

    const schema = config.schema ?? {};
    const includeDefault = schema.includeDefault ?? config.includeDefaultSchema ?? false;
    const schemaType = schema.type ?? config.schemaType ?? routeDefaults.schemaType;
    const faqList = schema.faqList ?? config.faqList;
    let breadcrumbJson = config.breadcrumbList;

    if (schema.breadcrumbItems?.length) {
        breadcrumbJson = breadcrumbListJson(schema.breadcrumbItems, locale, localeNeutralPath);
    }

    const schemaScripts: ResolvedPageSeo["schemaScripts"] = [];

    if (includeDefault && schemaType === "WebPage" && config.title && config.description) {
        const graph: Record<string, unknown>[] = [
            {
                "@type": "WebPage",
                name: config.title,
                description: config.description,
                url: canonicalUrl,
                inLanguage: getLocaleHreflang(locale)
            }
        ];

        if (breadcrumbJson) {
            graph.push({ "@type": "BreadcrumbList", itemListElement: JSON.parse(breadcrumbJson) });
        }

        schemaScripts.push({
            id: "jsonld-page",
            json: JSON.stringify({ "@context": "https://schema.org", "@graph": graph })
        });
    }

    if (faqList?.length) {
        schemaScripts.push({ id: "jsonld-faq", json: getFaqSchemaOrgJson({ quesAns: faqList }) });
    }

    if (!includeDefault && breadcrumbJson) {
        appendBreadcrumbSchema(schemaScripts, breadcrumbJson);
    }

    if (schema.extraJsonLd) {
        const entries = Array.isArray(schema.extraJsonLd) ? schema.extraJsonLd : [schema.extraJsonLd];
        entries.forEach((entry, index) => {
            schemaScripts.push({ id: `jsonld-extra-${index}`, json: JSON.stringify(entry) });
        });
    }

    return {
        title: config.title,
        description: config.description,
        keywords: config.keywords,
        canonicalUrl,
        ogUrl,
        ogDescription,
        ogImgSrc,
        ogImgAltText: config.ogImgAltText,
        ogType: config.ogType,
        ogSiteName: config.ogSiteName,
        ogLocale: getLocaleOgLocale(locale),
        ogLocaleAlternates: hreflangLocales.filter((entry) => entry !== locale).map(getLocaleOgLocale),
        shouldBeIndexed: config.shouldBeIndexed ?? true,
        hreflangAlternates: getHreflangAlternates(localeNeutralPath, hreflangLocales),
        hreflangDefault: getLocalizedPageUrl("en", localeNeutralPath),
        schemaScripts
    };
}
